// src/components/TopListHeaderBar.jsx
import React from "react";
import {
  Box,
  CardHeader,
  Typography,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import FigmaIcon, { getFigmaActionIcon } from "./icons/FigmaIcon";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  toolbarPillButtonSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";
import { createToolbarTextFieldSx } from "./toolbarControlStyles";

/**
 * actions: array of button configs
 * [
 *   {
 *     key: "filter",
 *     label: "Filter by",
 *     variant: "outlined" | "contained" | "text",
 *     startIcon: <SomeIcon />,
 *     onClick: () => {},
 *     sx: { ... },           // optional extra styles
 *     size: "small" | "medium" | "large",
 *     disabled: bool,
 *   },
 *   ...
 * ]
 *
 * Props added for tab badge (like screenshot):
 *  - tabLabel: string (e.g., "Leads", "Jobs")
 *  - tabCount: number|string (e.g., 3247)
 *  - showTab: boolean (default true when tabLabel exists)
 */
const TopListHeaderBar = ({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchIcon,
  actions = [],
  tabLabel,
  tabCount,
  showTab,
  resultsToolbarTargetId,
  tabContent,
}) => {
  const SHOW_TAB = Boolean(showTab ?? tabLabel);

  const BLUE = "#3451F4";
  const PILL_BG = "#E0F2FE";
  const UNDERLINE = "#3B82F6";
  const BASELINE = "#E5E7EB";

  return (
    <Box
      mb={1}
      className="side-padding-16px table-view-header"
      sx={{
        paddingTop: "12px",
        paddingBottom: "6px",
        display: "flex",
        flexDirection: "column",
        "& > [id]": { order: 3 },
      }}
    >
      {/* Top row (title + right controls) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 2,
          minWidth: 0,
        }}
      >
        <CardHeader
          className="topbar-title"
          title={<Typography variant="h6">{title}</Typography>}
          sx={{
            paddingLeft: 0,
            pb: 0.25,
            pt: 0,
            width: { xs: "100%", lg: "auto" },
            flex: { lg: "0 0 auto" },
            minWidth: 0,
            "& .MuiCardHeader-content": {
              minWidth: 0,
            },
            "& .MuiTypography-root": {
              overflowWrap: "anywhere",
            },
          }}
        />

        <Box
          className="sm-flex"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            width: { xs: "100%", lg: "auto" },
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            className="top-search-input"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={onSearchChange}
            sx={createToolbarTextFieldSx({
              widthSx: {
                width: { xs: "100%", md: 320, xl: 448 },
                maxWidth: "100%",
                minWidth: 0,
              },
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchIcon || <FigmaIcon name="search" />}
                </InputAdornment>
              ),
            }}
          />

          <Box
            className="inner-top-btns"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: { xs: "stretch", md: "flex-end" },
              gap: 1.5,
              width: { xs: "100%", md: "auto" },
              "& > .MuiButton-root": {
                width: { xs: "100%", sm: "auto" },
              },
            }}
          >
            {actions.map((action) => (
              <Button
                key={action.key || action.label}
                className={withPillButtonClassName(action.className)}
                variant={action.variant || "outlined"}
                size={action.size || "small"}
                startIcon={(action.startIcon ?? action.icon) ? (getFigmaActionIcon(action.label) ?? action.startIcon ?? action.icon) : (action.startIcon ?? action.icon)}
                onClick={action.onClick}
                disabled={action.disabled}
                sx={[
                  pillButtonInteractiveSx,
                  {
                    textTransform: "none",
                    borderRadius: 999,
                    whiteSpace: "nowrap",
                    ...(action.variant === "contained"
                      ? {
                          ...pillButtonFilledSx,
                        }
                      : {}),
                  },
                  action.variant === "contained" ? toolbarPillButtonSx : {},
                  action.sx || {},
                ]}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ✅ Tab row: ONLY the grey baseline is full width.
          The blue underline stays only under the tab label+count (like screenshot). */}
      {tabContent ? <Box sx={{ order: 2 }}>{tabContent}</Box> : null}
      {resultsToolbarTargetId ? <Box id={resultsToolbarTargetId} /> : null}

      {SHOW_TAB && (
        <Box sx={{ position: "relative", mt: 2, pb: 0, mb: 0, order: 2 }}>
          {/* Tab content (shrink to content width) */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              position: "relative",
              pb: 0.9, // space for underline
              
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: BLUE,
                lineHeight: 1,
              }}
            >
              {tabLabel}
            </Typography>

            {tabCount !== undefined && tabCount !== null && (
              <Box
                sx={{
                  px: 1,
                  py: "2px",
                  borderRadius: 999,
                  backgroundColor: PILL_BG,
                  color: BLUE,
                  fontSize: 11,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {tabCount}
              </Box>
            )}

            {/* Blue active underline (ONLY under the tab content) */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                bottom: 0,
                height: "3px",
                width: "100%",
                backgroundColor: UNDERLINE,
                marginBottom:"-1px",
                zIndex: 1

              }}
            />
          </Box>

          {/* Grey baseline FULL WIDTH */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,

              height: "1px",
              backgroundColor: BASELINE,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default TopListHeaderBar;
